import {
  Box,
  Flex,
  IconButton,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Input,
} from "@chakra-ui/react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
// Custom components
import Card from "components/card/Card";
import {
  IconArrowRight,
  IconChecked,
  IconCloseHamburgur,
  IconPencil,
} from "components/icons/Icons";
import {useState, useEffect, Fragment, useRef} from "react";
import { useDispatch, useSelector } from "react-redux";
import { allUsersSelector, isGettingAllUsersSelector } from "store/user/selector";
import _ from "lodash";
import { deleteUserRequest, updateUserRequest } from "store/user/reducer";
import DeleteButtopPopup from "./DeleteButtonPopup";

// Assets

type RowObj = {
  id: string;
  name: string;
  email: string;
  phone?: string;
};

const columnHelper = createColumnHelper<RowObj>();

// const columns = columnsDataCheck;
export default function AdminsTable(props: {
  tableData: any;
  onSelectRow: Function;
}) {
  const dispatch = useDispatch();
  const { tableData, onSelectRow } = props;
  const [sorting, setSorting] = useState<SortingState>([]);
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(
    null
  );
  const [editingColumnId, setEditingColumnId] = useState<string | null>(
    null
  );
  const isGettingAllUsers = useSelector(isGettingAllUsersSelector);
  const allUsers = useSelector(allUsersSelector);

  const inputRefs = useRef<{
    [key: string]: HTMLInputElement;
  }>({});

  const handleInputChange = (
    rowIndex: number,
    columnId: keyof RowObj,
    value: string | number
  ) => {
    setEditingColumnId(columnId);
    setRawData((prevData) =>
      prevData.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: value } : row
      )
    );
  };

  const handleToggleEdit = (rowIndex: number) => {
    setEditingRowIndex(rowIndex === editingRowIndex ? null : rowIndex);
    setRawData(data);
  };

  const handleDelete = (rowIndex: number) => {
    const userRow = rawData[rowIndex];

    dispatch(
      deleteUserRequest({
        user_id: userRow?.id
      })
    )
    setData(() => (
      rawData.filter((_, index) => index !== rowIndex)
    ));
  }

  const handleSaveData = (rowIndex: number) => {
    // api call to save data;
    const userRow = rawData[rowIndex];

    dispatch(
      updateUserRequest({
        user_id: userRow?.id,
        user_details: {
          first_name: userRow?.name?.trim().split(' ')[0] ?? "",
          last_name: userRow?.name?.trim().split(' ')[1] ?? "",
          email: userRow?.email,
          phone: userRow?.phone
        },
        no_after_call: true
      })
    )
    setData(rawData);
    initializeEditSetting();
  };

  const initializeEditSetting = () => {
    setEditingColumnId(null);
    setEditingRowIndex(null);
  };

  let defaultData = tableData;
  const columns = [
    columnHelper.accessor("name", {
      id: "name",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          lineHeight="24px"
          fontWeight="400"
          color="gray.500"
        >
          Name
        </Text>
      ),
      cell: ({ row, getValue }) => {
        const isEditing = editingRowIndex === row.index;
        return isEditing ? (
          <Input
            ref={(el) => (inputRefs.current[row.index + "name"] = el!)}
            value={rawData[row.index].name}
            onChange={(e) =>
              handleInputChange(row.index, "name", e.target.value)
            }
          />
        ) : (
          <Text
            fontSize={"14px"}
            lineHeight="24px"
            fontWeight={"400"}
            color="black"
          >
            {getValue()}
          </Text>
        );
      },
    }),
    columnHelper.accessor("email", {
      id: "email",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          lineHeight="24px"
          fontWeight="400"
          color="gray.500"
        >
          Email
        </Text>
      ),
      cell: ({ row, getValue }) => {
        const isEditing = editingRowIndex === row.index;
        return isEditing ? (
          <Input
            ref={(el) => (inputRefs.current[row.index + "email"] = el!)}
            value={rawData[row.index].email}
            onChange={(e) =>
              handleInputChange(row.index, "email", e.target.value)
            }
          />
        ) : (
          <Text
            fontSize={"14px"}
            lineHeight="24px"
            fontWeight={"400"}
            color="black"
          >
            {getValue()}
          </Text>
        );
      },
    }),
    columnHelper.accessor("phone", {
      id: "phone",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          lineHeight="24px"
          fontWeight="400"
          color="gray.500"
        >
          Phone
        </Text>
      ),
      cell: ({ row, getValue }) => {
        const isEditing = editingRowIndex === row.index;
        return isEditing ? (
          <Input
            ref={(el) => (inputRefs.current[row.index + "phone"] = el!)}
            value={rawData[row.index].phone}
            onChange={(e) =>
              handleInputChange(row.index, "phone", e.target.value)
            }
          />
        ) : (
          <Text
            fontSize={"14px"}
            lineHeight="24px"
            fontWeight={"400"}
            color="black"
          >
            {getValue()}
          </Text>
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          lineHeight="24px"
          fontWeight="400"
          color="gray.500"
        >
          Actions
        </Text>
      ),
      cell: ({ row }) => {
        const isEditing = editingRowIndex === row.index;
        return (
          <>
            {!isEditing ? (
              <Flex align="center">
                <IconButton
                  aria-label="edit"
                  bg="inherit"
                  borderRadius="inherit"
                  onClick={() => {
                    handleToggleEdit(row.index);
                  }}
                  icon={<IconPencil width="25px" height="25px" />}
                />
                <DeleteButtopPopup displayMode="desktop" onOk={() => handleDelete(row.index)}/>
                <IconButton
                  aria-label="detail"
                  bg="inherit"
                  borderRadius="inherit"
                  onClick={() => {
                    onSelectRow(row.original.id);
                  }}
                  icon={<IconArrowRight width="40px" height="40px" />}
                />
              </Flex>
            ) : (
              <Flex align="center">
                <IconButton
                  aria-label="save"
                  bg="inherit"
                  borderRadius="inherit"
                  onClick={() => {
                    handleSaveData(row.index);
                  }}
                  icon={<IconChecked width="40px" height="40px" />}
                />
                <IconButton
                  aria-label="cancel"
                  bg="inherit"
                  borderRadius="inherit"
                  onClick={() => {
                    initializeEditSetting();
                  }}
                  icon={<IconCloseHamburgur width="40px" height="40px" />}
                />
              </Flex>
            )}
          </>
        );
      },
    }),
  ];

  const [data, setData] = useState(() => [...defaultData]);
  const [rawData, setRawData] = useState(() => [...data]);

  useEffect(() => {
    setData([...defaultData]);
  }, [defaultData]);

  useEffect(() => {
    setRawData([...data]);
  }, [data]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  useEffect(() => {
    if (
      editingRowIndex != null &&
      editingColumnId != null &&
      inputRefs.current[editingRowIndex + editingColumnId]
    ) {
      inputRefs.current[editingRowIndex + editingColumnId].focus();
    }
  }, [rawData, editingRowIndex, editingColumnId]);

  const mobileColumnWidths: any = {
    name: 120,
    email: 120,
    phone: 150,
    description: 250,
    city: 80,
    country: 80,
  };
  return (
    <Card
      flexDirection="column"
      p="0px"
      px="10px"
      w="calc(100% + 20px)"
      ml="-10px"
      overflowX="auto"
    >
      <Box>
        <Table variant="simple" color="gray.500" mb="24px">
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <Th
                      key={header.id}
                      colSpan={header.colSpan}
                      px={{ lg: "20px", md: "10px", base: "10px" }}
                      border={"none"}
                      fontFamily="Inter"
                      cursor="pointer"
                      textTransform={"capitalize"}
                    >
                      <Flex
                        justifyContent="space-between"
                        align="center"
                        fontSize={{ sm: "10px", lg: "12px" }}
                        lineHeight="24px"
                        color="gray.500"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: "",
                          desc: "",
                        }[header.column.getIsSorted() as string] ?? null}
                      </Flex>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {!isGettingAllUsers &&
              table
                .getRowModel()
                .rows.slice(0, 11)
                .map((row, index: number) => {
                  return (
                    <Fragment key={index}>
                      <Tr
                        mb={"10px"}
                        position="relative"
                        zIndex={1}
                        cursor="pointer"
                        onClick={() => {
                          // onSelectRow(row.original.id);
                        }}
                        _after={{
                          content: '""',
                          position: "absolute",
                          border: "1px solid #DCDCDC",
                          borderRadius: "10px",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          zIndex: "-1",
                          transition: "all .25s ease-in-out",
                        }}
                        _hover={{
                          _after: {
                            backgroundColor: "#F1F1F1",
                            borderColor: "#F1F1F1",
                            boxShadow: "0px 2px 6px 3px rgba(0, 0, 0, 0.1)",
                          },
                        }}
                        _active={{
                          _after: {
                            boxShadow: "none",
                          },
                        }}
                      >
                        {row.getVisibleCells().map((cell, index: number) => {
                          return (
                            <Td
                              key={index}
                              padding={{
                                lg: "20px",
                                md: "20px 10px",
                                base: "15px 10px",
                              }}
                              fontSize={{ sm: "14px" }}
                              minW={{
                                base: `${mobileColumnWidths[cell.column.id]}px`,
                                md: `${mobileColumnWidths[cell.column.id]}px`,
                                lg: "auto",
                              }}
                              border="none"
                              width={
                                cell.column.id === "actionCol" ? "80px" : "auto"
                              }
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </Td>
                          );
                        })}
                      </Tr>
                      <Tr>
                        <Td py={1} borderBottom="none"></Td>
                      </Tr>
                    </Fragment>
                  );
                })}
            {isGettingAllUsers &&
              [1, 2, 3, 4, 5].map((row) => {
                return (
                  <Fragment key={`skeleton-row-${row}`}>
                    <Tr
                      zIndex="1"
                      position="relative"
                      _after={{
                        content: '""',
                        position: "absolute",
                        border: "1px solid #DCDCDC",
                        borderRadius: "10px",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        zIndex: "-1",
                        transition: "all .25s ease-in-out",
                      }}
                    >
                      {columns.map((it: any, colIndex: number) => {
                        return (
                          <Td
                            key={`skeleton-${colIndex}`}
                            padding={{
                              lg: "20px",
                              md: "20px 10px",
                              base: "15px 10px",
                            }}
                            border="none !important"
                          >
                            <Skeleton
                              height="24px"
                              colorScheme="#F3F5F5"
                              borderRadius="5px"
                            ></Skeleton>
                          </Td>
                        );
                      })}
                    </Tr>
                    <Tr>
                      <Td
                        colSpan={7}
                        paddingTop="5px"
                        paddingBottom="10px"
                        border="none !important"
                      ></Td>
                    </Tr>
                  </Fragment>
                );
              })}{" "}
          </Tbody>
        </Table>
      </Box>
    </Card>
  );
}
